import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../assets.css';
import '../../vulnerabilities/vulnerabilities.css';
import { getInventoryDetail, toggleInventoryExclusion, deleteInventoryAsset } from '../../../services/asset.service';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import {
  ASSETS_INVENTORY_TABLE_COLUMNS,
  ASSETS_INVENTORY_TABLE_COLUMN_WIDTHS_KEY,
  ASSET_DETAIL_CERT_TABLE_COLUMNS,
  ASSET_DETAIL_CERT_TABLE_COLUMN_WIDTHS_KEY,
  ASSET_DETAIL_VULN_TABLE_COLUMNS,
  ASSET_DETAIL_VULN_TABLE_COLUMN_WIDTHS_KEY,
} from '../../../utils/resizableTableColumns';
import { getTableSortIcon } from '../../../utils/tableSort';
import { ResizableColgroup, ResizableTableHead } from '../../../components/ResizableTableHead';
import BlueXLoader, { PageDataLoader } from '../../../components/BlueXLoader';
import { TrashIcon } from '../../../shared/components/TrashIcon';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';
import { useNavigate } from 'react-router-dom';
import { riskDetailPath } from '../../../routes/dashboardRoutes';

/**
 * Compact inventory-shell table for Asset Detail modal sections.
 * Matches Risks / Assets / Scan Detail table chrome (sticky header + scroll body).
 */
function AssetDetailEmbeddedTable({
  columns = null,
  widths = null,
  onResizeStart = null,
  resizingKey = null,
  sortConfig = null,
  onSort = null,
  getSortIcon = null,
  tableMinWidth = null,
  headerLabel = null,
  hideHeader = false,
  emptyMessage = 'No data found',
  colSpan = 1,
  children,
  className = '',
}) {
  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);
  const isResizable = Array.isArray(columns) && columns.length > 0 && widths;

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;
    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };
    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [children]);

  const tableClassName = [
    'assets-table',
    'risks-page-table',
    isResizable ? 'risks-page-table--resizable' : null,
    'assets-detail-embedded-table',
  ]
    .filter(Boolean)
    .join(' ');
  const tableStyle = {
    width: '100%',
    minWidth: tableMinWidth || (isResizable ? undefined : '100%'),
  };

  return (
    <div
      className={[
        'assets-table-shell',
        'assets-table-shell--inventory-body',
        'assets-detail-embedded-table-shell',
        hideHeader ? 'assets-detail-embedded-table-shell--no-header' : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {hideHeader ? null : (
        <div
          className="risks-page-table-header-frame"
          style={{ '--risks-header-gutter': `${headerGutterPx}px` }}
        >
          <div ref={tableHeaderRef} className="risks-page-table-header">
            <table className={`${tableClassName} risks-page-table--header`} style={tableStyle}>
              {isResizable ? <ResizableColgroup columns={columns} widths={widths} /> : null}
              {isResizable ? (
                <ResizableTableHead
                  columns={columns}
                  widths={widths}
                  onResizeStart={onResizeStart}
                  resizingKey={resizingKey}
                  sortConfig={sortConfig}
                  onSort={onSort}
                  getSortIcon={getSortIcon}
                  innerClassName="risks-page-th-inner"
                  labelClassName="risks-page-th-label"
                  resizeHandleClassName="risks-page-col-resize-handle"
                  defaultSortButtonClassName="assets-sort-btn risks-page-sort-btn"
                  defaultSortIconClassName="assets-sort-icon"
                />
              ) : (
                <thead>
                  <tr>
                    <th>
                      <div className="risks-page-th-inner">
                        <span className="risks-page-th-label">{headerLabel || ''}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
              )}
            </table>
          </div>
          <div className="risks-page-table-header-gutter" aria-hidden="true" />
        </div>
      )}
      <div
        ref={tableScrollRef}
        className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
        onScroll={hideHeader ? undefined : handleTableScroll}
      >
        <table className={`${tableClassName} risks-page-table--body`} style={tableStyle}>
          {isResizable ? <ResizableColgroup columns={columns} widths={widths} /> : null}
          <tbody>
            {children || (
              <tr>
                <td colSpan={isResizable ? columns.length : colSpan} className="assets-empty-cell">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Match Scans table sort affordance (⇅ / ↑ / ↓). */
function getScansStyleSortIcon(sortConfig, key) {
  if (sortConfig?.key !== key) return '⇅';
  return sortConfig.direction === 'asc' ? '↑' : '↓';
}

function displayOrNA(value) {
  const text = String(value ?? '').trim();
  return text || 'NA';
}

function assetRowKey(row) {
  const id = String(row?.assetId ?? '').trim();
  if (id) return id;
  return `${row?.hostname || 'asset'}|${row?.ipAddress || ''}|${row?.site || ''}|${row?.macAddress || ''}`;
}

function severitySortValue(raw) {
  const numeric = Number.parseFloat(raw);
  if (Number.isFinite(numeric)) return numeric;
  const label = String(raw || '').trim().toLowerCase();
  if (label === 'critical') return 9.5;
  if (label === 'high') return 7.5;
  if (label === 'medium') return 5.0;
  if (label === 'low' || label === 'log' || label === 'info' || label === 'informational') return 2.0;
  return 0;
}

/** Map CVSS / severity label to badge tone (low < 4, medium 4–6.9, high 7–8.9, critical 9+). */
function severityBadgeClass(rawValue) {
  const value = severitySortValue(rawValue);
  if (value < 4.0) return 'severity-low';
  if (value <= 6.9) return 'severity-medium';
  if (value <= 8.9) return 'severity-high';
  return 'severity-critical';
}

function buildDashboardVulnRows(propData) {
  if (Array.isArray(propData) && propData.length > 0) {
    return propData.map((row) => {
      const severityFloat = severitySortValue(row.severity_float ?? row.severity);
      return {
        title: row.title ?? '',
        // Prefer CVSS float when present so the Severity column stays numeric/sortable.
        severity:
          row.severity_float != null && Number.isFinite(Number(row.severity_float))
            ? String(row.severity_float)
            : severityFloat
              ? String(severityFloat)
              : (row.severity != null ? String(row.severity) : ''),
              technicalImpact: row.technical_impact,
              businessImpact: row.business_impact,
        count: Number(row.count) || 0,
      };
    });
  }
  return [];
}

/**
 * Sortable table of open vulnerabilities. Data from API (prop).
 * Normalize dashboard vulnerability rows and always render as text (no raw HTML) for security.
 */
const VulnerabilitiesTable = ({
  data: propData,
  universalSearchQuery = '',
  onNavigateToRisks,
  onNavigateToScans,
  isLoading: isLoadingProp,
  loadError = '',
}) => {
  // Default sort: highest severity first (High / Critical at the top).
  const [sortConfig, setSortConfig] = useState({ key: 'severity', direction: 'desc' });
  const isLoading = isLoadingProp ?? propData == null;

  const data = useMemo(
    () => (isLoading ? [] : buildDashboardVulnRows(propData)),
    [propData, isLoading]
  );

  const dataFiltered = useMemo(() => {
    const q = String(universalSearchQuery || '').trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const blob = [
        row.title,
        row.severity,
        displayOrNA(row.technicalImpact),
        displayOrNA(row.businessImpact),
        String(row.count ?? ''),
      ].join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [data, universalSearchQuery]);

  const getSeverityClass = severityBadgeClass;

  const getSeverityBucket = (rawValue) => {
    const value = severitySortValue(rawValue);
    if (value < 4.0) return 'Low';
    if (value <= 6.9) return 'Medium';
    if (value <= 8.9) return 'High';
    return 'Critical';
  };

  const handleOpenVulnerability = (row) => {
    const title = String(row?.title || '').trim();
    const severity = getSeverityBucket(row?.severity);
    if (typeof onNavigateToRisks === 'function') {
      onNavigateToRisks({
        status: 'open',
        severity: severity || undefined,
        q: title || undefined,
      });
      return;
    }
    // Legacy fallback — prefer risks navigation above.
    if (typeof onNavigateToScans === 'function') onNavigateToScans('all');
  };

  const sortedData = useMemo(() => {
    const items = [...dataFiltered];
    items.sort((a, b) => {
      let result = 0;
      if (sortConfig.key === 'title') {
        result = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      } else if (sortConfig.key === 'severity') {
        result = severitySortValue(a.severity) - severitySortValue(b.severity);
      } else if (sortConfig.key === 'technicalImpact' || sortConfig.key === 'businessImpact') {
        result = (Number(a[sortConfig.key]) || 0) - (Number(b[sortConfig.key]) || 0);
      } else if (sortConfig.key === 'count') {
        result = (a.count || 0) - (b.count || 0);
      }

      return sortConfig.direction === 'asc' ? result : -result;
    });
    return items;
  }, [dataFiltered, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIcon = (key) => getScansStyleSortIcon(sortConfig, key);

  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [isLoading, sortedData.length]);

  const headerCells = (
    <tr>
      <th>
        <div className="vuln-th-inner">
          <button type="button" className="assets-sort-btn vuln-sort-btn" onClick={() => handleSort('title')}>
            TITLE
            <span className={`assets-sort-icon vuln-sort-icon${sortConfig.key === 'title' ? ' is-active' : ''}`} aria-hidden="true">
              {getSortIcon('title')}
            </span>
          </button>
        </div>
      </th>
      {/* <th className="assets-cell-center">
        <div className="vuln-th-inner vuln-th-inner--center">
          <button type="button" className="assets-sort-btn vuln-sort-btn" onClick={() => handleSort('technicalImpact')}>
            TECHNICAL IMPACT
            <span className={`assets-sort-icon vuln-sort-icon${sortConfig.key === 'technicalImpact' ? ' is-active' : ''}`} aria-hidden="true">
              {getSortIcon('technicalImpact')}
            </span>
          </button>
        </div>
      </th>
      <th className="assets-cell-center">
        <div className="vuln-th-inner vuln-th-inner--center">
          <button type="button" className="assets-sort-btn vuln-sort-btn" onClick={() => handleSort('businessImpact')}>
            BUSINESS IMPACT
            <span className={`assets-sort-icon vuln-sort-icon${sortConfig.key === 'businessImpact' ? ' is-active' : ''}`} aria-hidden="true">
              {getSortIcon('businessImpact')}
            </span>
          </button>
        </div>
      </th> */}
      <th className="assets-cell-center">
        <div className="vuln-th-inner vuln-th-inner--center">
          <button type="button" className="assets-sort-btn vuln-sort-btn" onClick={() => handleSort('severity')}>
            SEVERITY
            <span className={`assets-sort-icon vuln-sort-icon${sortConfig.key === 'severity' ? ' is-active' : ''}`} aria-hidden="true">
              {getSortIcon('severity')}
            </span>
          </button>
        </div>
      </th>
      <th className="assets-cell-center">
        <div className="vuln-th-inner vuln-th-inner--center">
          <button type="button" className="assets-sort-btn vuln-sort-btn" onClick={() => handleSort('count')}>
            COUNT
            <span className={`assets-sort-icon vuln-sort-icon${sortConfig.key === 'count' ? ' is-active' : ''}`} aria-hidden="true">
              {getSortIcon('count')}
            </span>
          </button>
        </div>
      </th>
    </tr>
  );

  return (
    <div className="vulnerabilities-section">
      <div className="vuln-table-card">
        <h3 className="vuln-section-title">Open vulnerabilities by severity</h3>
        <div className="assets-table-shell assets-table-shell--inventory-body vuln-table-shell">
          <div
            className="vuln-table-header-frame"
            style={{ '--vuln-header-gutter': `${headerGutterPx}px` }}
          >
            <div ref={tableHeaderRef} className="vuln-table-header">
              <table className="assets-table assets-inventory-table vuln-table vuln-table--header">
                <colgroup>
                  <col className="col-title" />
                  {/*<col className="col-impact" />
                  <col className="col-impact" /> */}
                  <col className="col-severity" />
                  <col className="col-count" />
                </colgroup>
                <thead>{headerCells}</thead>
              </table>
            </div>
            <div className="vuln-table-header-gutter" aria-hidden="true" />
          </div>
          <div
            ref={tableScrollRef}
            className="assets-inventory-table-scroll assets-inventory-table-scroll--body vuln-table-scroll"
            onScroll={handleTableScroll}
          >
            <table className="assets-table assets-inventory-table vuln-table vuln-table--body">
              <colgroup>
                <col className="col-title" />
                {/* <col className="col-impact" />
                <col className="col-impact" /> */}
                <col className="col-severity" />
                <col className="col-count" />
              </colgroup>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={3} className="assets-empty-cell vuln-empty-cell">
                      <BlueXLoader size="small" />
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan={3} className="assets-empty-cell vuln-empty-cell" role="alert">
                      {loadError}
                    </td>
                  </tr>
                ) : sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="assets-empty-cell vuln-empty-cell">
                      No open vulnerabilities found for this scope.
                    </td>
                  </tr>
                ) : (
                  sortedData.map((row, idx) => (
                    <tr key={idx}>
                      <td className="vuln-title-cell">
                        <a
                          href="/app/risks"
                          onClick={(e) => {
                            e.preventDefault();
                            handleOpenVulnerability(row);
                          }}
                          className="vuln-title-link"
                          title={`View open ${getSeverityBucket(row.severity).toLowerCase()} findings for ${row.title}`}
                        >
                          {row.title}
                        </a>
                      </td>
                      {/* <td className="assets-cell-center vuln-impact-cell">{displayOrNA(row.technicalImpact)}</td>
                      <td className="assets-cell-center vuln-impact-cell">{displayOrNA(row.businessImpact)}</td> */}
                      <td className="assets-cell-center">
                        <span className={`vuln-severity-badge ${getSeverityClass(row.severity)}`}>
                          {row.severity}
                        </span>
                      </td>
                      <td className="assets-cell-center vuln-count-cell">{row.count}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

function buildAssetTableRows(propData) {
  if (propData == null || !Array.isArray(propData)) {
    return [];
  }

  if (propData.length === 0) {
    return [];
  }

  const looksLikeInventory = propData[0] && Object.prototype.hasOwnProperty.call(propData[0], 'ip');
  if (!looksLikeInventory) return propData;

  const normalizeCertificatesCount = (certificates) => {
    if (!certificates) return 0;
    if (Array.isArray(certificates)) return certificates.length;
    if (typeof certificates === 'object') return Object.keys(certificates).length;
    return 0;
  };

  const formatLastScan = (lastScanDate) => {
    if (!lastScanDate) return '';
    const d = new Date(lastScanDate);
    if (Number.isNaN(d.getTime())) return String(lastScanDate).slice(0, 10);
    return d.toISOString().slice(0, 10);
  };

  const deriveType = (inv) => {
    if (inv?.webapp === true) return 'Web App';
    if (inv?.on_prem === true) return 'On-Premise';
    const rawType = String(inv?.device_type || inv?.type || '').trim().toLowerCase();
    if (rawType === 'web app' || rawType === 'webapp') return 'Web App';
    if (rawType === 'on-prem' || rawType === 'onprem' || rawType === 'on-premise' || rawType === 'on premise') {
      return 'On-Premise';
    }
    if (rawType === 'perimeter') return 'Perimeter';
    return 'Perimeter';
  };

  const resolveAssetId = (inv) =>
    inv?.uu_id
    ?? inv?.inventory_uuid
    ?? inv?.inventory_id
    ?? inv?.id
    ?? (inv?.schedule_uuid ? `schedule:${inv.schedule_uuid}` : null);

  return propData.map((inv) => ({
    hostname: inv.hostname ?? '',
    ipAddress: inv.ip ?? '',
    org:
      inv.org_name
      ?? inv.organization_name
      ?? (typeof inv.organization === 'string' ? inv.organization : inv.organization?.name)
      ?? '',
    site: inv.site_name ?? '',
    siteUuid: inv.site_uuid ?? '',
    type: deriveType(inv),
    os: inv.host_os ?? '',
    software: inv.host_software ?? '',
    macAddress: inv.mac_address ?? inv.macAddress ?? inv.mac ?? '',
    manufacturer: inv.mac_vendor ?? inv.macVendor ?? '',
    certificates: normalizeCertificatesCount(inv.certificates ?? inv.certificate_count),
    vulnerabilities: Number(inv.total_vul ?? 0) || 0,
    lastScan: formatLastScan(inv.last_scan_date),
    ipExclusion: inv.is_excluded ? 'Yes' : 'No',
    assetId: resolveAssetId(inv),
    openPorts: inv.open_ports ?? '',
    excludedReason: inv.excluded_reason ?? '',
  }));
}

const UNIVERSAL_SEARCH_RESULTS_CAP = 8;

function universalSearchTextMatches(parts, queryRaw) {
  const needle = String(queryRaw || '').trim().toLowerCase();
  if (!needle) return false;
  return parts
    .map((p) => String(p ?? ''))
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

const AssetsTable = ({
  data: propData,
  universalSearchQuery = '',
  focusSeed = null,
  modalOnly = false,
  onDetailClose = null,
  isLoading: isLoadingProp,
  onInventoryRefresh = null,
}) => {
  const navigate = useNavigate();
  const isLoading = isLoadingProp ?? propData == null;
  const [sortConfig, setSortConfig] = useState({ key: 'hostname', direction: 'asc' });
  const [tableSearch, setTableSearch] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [exclusionOverrides, setExclusionOverrides] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowActionBusyId, setRowActionBusyId] = useState(null);
  const [bulkActionMessage, setBulkActionMessage] = useState('');
  const [exclusionConfirm, setExclusionConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [openRowMenuKey, setOpenRowMenuKey] = useState(null);
  const [rowMenuPlacement, setRowMenuPlacement] = useState(null);
  const [assetDetailOpen, setAssetDetailOpen] = useState(false);
  const [assetDetailLoading, setAssetDetailLoading] = useState(false);
  const [assetDetailError, setAssetDetailError] = useState(null);
  const [assetDetail, setAssetDetail] = useState(null);
  const [assetDetailTargetId, setAssetDetailTargetId] = useState(null);
  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const selectAllRef = useRef(null);
  const rowActionsMenuRef = useRef(null);
  const rowActionsTriggerRef = useRef(null);
  const openAssetDetailRef = useRef(null);
  const closeAssetDetailRef = useRef(null);
  const tourAssetRowsRef = useRef([]);
  const lastFocusSeedNonceRef = useRef(0);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);
  const tableSearchId = useId();
  const assetDetailVulnSearchId = useId();
  const exclusionConfirmDescId = useId();
  const deleteConfirmDescId = useId();
  const assetColumns = useMemo(
    () => ASSETS_INVENTORY_TABLE_COLUMNS.map((col) => ({ ...col })),
    []
  );
  const {
    widths: assetColumnWidths,
    onResizeStart: onAssetColumnResizeStart,
    tableMinWidth: assetsTableMinWidth,
    resizingKey: assetColumnResizingKey,
  } = useTableColumnResize(assetColumns, ASSETS_INVENTORY_TABLE_COLUMN_WIDTHS_KEY);

  const rows = useMemo(() => {
    if (isLoading) return [];
    return buildAssetTableRows(propData).map((row) => {
      const id = String(row?.assetId ?? '').trim();
      if (!id || !Object.prototype.hasOwnProperty.call(exclusionOverrides, id)) return row;
      return {
        ...row,
        ipExclusion: exclusionOverrides[id] ? 'Yes' : 'No',
      };
    });
  }, [propData, isLoading, exclusionOverrides]);

  const rowsForSort = useMemo(() => {
    const q = String(tableSearch || universalSearchQuery || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const blob = [
        r.hostname,
        r.ipAddress,
        r.org,
        r.site,
        r.type,
        r.macAddress,
        r.os,
        r.software,
        r.manufacturer,
        String(r.certificates ?? ''),
        String(r.vulnerabilities ?? ''),
        r.lastScan,
        r.openPorts,
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rows, tableSearch, universalSearchQuery]);

  const parseIPv4 = (ip) => {
    if (typeof ip !== 'string') return null;
    const parts = ip.split('.');
    if (parts.length !== 4) return null;
    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => Number.isNaN(n))) return null;
    return nums.reduce((acc, n) => acc * 256 + (n & 255), 0);
  };

  const sortedRows = useMemo(() => {
    const compareByKey = (a, b, key) => {
      const aVal = a?.[key];
      const bVal = b?.[key];

      if (key === 'certificates' || key === 'vulnerabilities') {
        const aNum = Number(aVal) || 0;
        const bNum = Number(bVal) || 0;
        return aNum - bNum;
      }

      if (key === 'lastScan') {
        const aTime = Date.parse(String(aVal ?? '')) || 0;
        const bTime = Date.parse(String(bVal ?? '')) || 0;
        return aTime - bTime;
      }

      if (key === 'ipAddress') {
        const aIp = parseIPv4(aVal);
        const bIp = parseIPv4(bVal);
        // If parsing fails, fall back to string compare.
        if (aIp != null && bIp != null) return aIp - bIp;
      }

      if (key === 'orgSite' || key === 'site') {
        return `${a?.org || ''} ${a?.site || ''}`.localeCompare(
          `${b?.org || ''} ${b?.site || ''}`,
          undefined,
          { sensitivity: 'base' }
        );
      }

      const aStr = String(aVal ?? '').toLowerCase();
      const bStr = String(bVal ?? '').toLowerCase();
      return aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
    };

    const items = rowsForSort.map((r, idx) => ({ r, idx }));
    items.sort((x, y) => {
      const result = compareByKey(x.r, y.r, sortConfig.key);
      if (result !== 0) return sortConfig.direction === 'asc' ? result : -result;
      // Stable tie-break.
      return x.idx - y.idx;
    });
    return items.map((it) => it.r);
  }, [rowsForSort, sortConfig]);
  const assetPagination = usePaginatedRows(sortedRows, TABLE_PAGE_SIZE);
  const visibleAssetRows = assetPagination.pageRows;
  tourAssetRowsRef.current = visibleAssetRows;

  const closeRowActionsMenu = useCallback(() => {
    setOpenRowMenuKey(null);
    setRowMenuPlacement(null);
  }, []);

  useEffect(() => {
    if (!openRowMenuKey) return undefined;
    const onPointerDown = (event) => {
      const inMenu = rowActionsMenuRef.current?.contains(event.target);
      const inTrigger = rowActionsTriggerRef.current?.contains(event.target);
      if (!inMenu && !inTrigger) closeRowActionsMenu();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeRowActionsMenu();
    };
    const onRepositionClose = () => closeRowActionsMenu();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onRepositionClose);
    const scrollEl = tableScrollRef.current;
    scrollEl?.addEventListener('scroll', onRepositionClose, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onRepositionClose);
      scrollEl?.removeEventListener('scroll', onRepositionClose);
    };
  }, [openRowMenuKey, closeRowActionsMenu]);

  useEffect(() => {
    closeRowActionsMenu();
  }, [assetPagination.page, sortConfig.key, sortConfig.direction, tableSearch, universalSearchQuery, closeRowActionsMenu]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIcon = useCallback((key) => getScansStyleSortIcon(sortConfig, key), [sortConfig]);

  const toggleRowSelected = useCallback((key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    const visibleKeys = visibleAssetRows.map((r) => assetRowKey(r));
    const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys.has(k));
    setSelectedKeys(allSelected ? new Set() : new Set(visibleKeys));
  }, [visibleAssetRows, selectedKeys]);

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    const total = visibleAssetRows.length;
    const sel = visibleAssetRows.filter((r) => selectedKeys.has(assetRowKey(r))).length;
    el.checked = total > 0 && sel === total;
    el.indeterminate = sel > 0 && sel < total;
  }, [visibleAssetRows, selectedKeys]);

  useEffect(() => {
    // Drop selections that are no longer in the current dataset.
    const valid = new Set(rows.map((r) => assetRowKey(r)));
    setSelectedKeys((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((key) => {
        if (valid.has(key)) next.add(key);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [rows]);

  useEffect(() => {
    // Server inventory is source of truth after reload/refresh.
    setExclusionOverrides({});
  }, [propData]);

  assetColumns[0].headerContent = (
    <span data-tour="assets-select-all">
      <input
        type="checkbox"
        ref={selectAllRef}
        className="risks-page-checkbox"
        aria-label="Select all visible assets"
        onChange={toggleSelectAllVisible}
        disabled={isLoading || sortedRows.length === 0}
      />
    </span>
  );

  const selectedRows = useMemo(
    () => sortedRows.filter((r) => selectedKeys.has(assetRowKey(r))),
    [sortedRows, selectedKeys]
  );
  const selectedCount = selectedRows.length;
  const bulkExcludableRows = useMemo(
    () =>
      selectedRows.filter((r) => {
        const id = String(r?.assetId ?? '').trim();
        return Boolean(id) && !id.startsWith('demo-');
      }),
    [selectedRows]
  );
  const bulkAllExcluded =
    bulkExcludableRows.length > 0 &&
    bulkExcludableRows.every((r) => String(r.ipExclusion || '').toLowerCase() === 'yes');
  const bulkExclusionLabel = bulkAllExcluded ? 'Include' : 'Exclude';

  const exclusionConfirmBusy = Boolean(bulkBusy || rowActionBusyId || (exclusionConfirm?.source === 'detail' && assetDetailLoading));
  const deleteConfirmBusy = Boolean(
    (rowActionBusyId && deleteConfirm) || (bulkBusy && deleteConfirm?.source === 'bulk')
  );

  const closeExclusionConfirm = useCallback(() => {
    if (exclusionConfirmBusy) return;
    setExclusionConfirm(null);
  }, [exclusionConfirmBusy]);

  const requestBulkExclusion = useCallback(() => {
    if (bulkBusy || bulkExcludableRows.length === 0 || deleteConfirm) return;
    const wantExcluded = !bulkAllExcluded;
    const targets = bulkExcludableRows.filter((r) => {
      const isExcluded = String(r.ipExclusion || '').toLowerCase() === 'yes';
      return wantExcluded ? !isExcluded : isExcluded;
    });
    if (targets.length === 0) {
      setBulkActionMessage(
        wantExcluded
          ? 'Selected assets are already excluded.'
          : 'Selected assets are already included.'
      );
      return;
    }
    setExclusionConfirm({
      source: 'bulk',
      wantExcluded,
      rows: targets,
    });
  }, [bulkBusy, bulkExcludableRows, bulkAllExcluded, deleteConfirm]);

  const requestBulkDelete = useCallback(() => {
    if (bulkBusy || bulkExcludableRows.length === 0 || exclusionConfirm || rowActionBusyId) return;
    setDeleteConfirm({
      source: 'bulk',
      rows: bulkExcludableRows,
    });
  }, [bulkBusy, bulkExcludableRows, exclusionConfirm, rowActionBusyId]);

  const requestRowExclusion = useCallback(
    (row) => {
      const id = String(row?.assetId ?? '').trim();
      if (!id || id.startsWith('demo-') || rowActionBusyId || bulkBusy || deleteConfirm) {
        if (!id || id.startsWith('demo-')) {
          setBulkActionMessage('Exclusion is only available for inventory-backed assets.');
        }
        return;
      }
      const isExcluded = String(row.ipExclusion || '').toLowerCase() === 'yes';
      setExclusionConfirm({
        source: 'row',
        wantExcluded: !isExcluded,
        rows: [row],
      });
    },
    [rowActionBusyId, bulkBusy, deleteConfirm]
  );

  const requestRowDelete = useCallback(
    (row) => {
      const id = String(row?.assetId ?? '').trim();
      if (!id || id.startsWith('demo-') || rowActionBusyId || bulkBusy || exclusionConfirm) {
        if (!id || id.startsWith('demo-')) {
          setBulkActionMessage('Delete is only available for inventory-backed assets.');
        }
        return;
      }
      setDeleteConfirm({
        source: 'row',
        rows: [row],
      });
    },
    [rowActionBusyId, bulkBusy, exclusionConfirm]
  );

  const closeDeleteConfirm = useCallback(() => {
    if (deleteConfirmBusy) return;
    setDeleteConfirm(null);
  }, [deleteConfirmBusy]);

  const confirmDeleteAsset = useCallback(async () => {
    if (!deleteConfirm || rowActionBusyId || bulkBusy) return;
    const targets = Array.isArray(deleteConfirm.rows)
      ? deleteConfirm.rows
      : deleteConfirm.row
        ? [deleteConfirm.row]
        : [];
    if (targets.length === 0) {
      setDeleteConfirm(null);
      return;
    }

    const isBulk = deleteConfirm.source === 'bulk' || targets.length > 1;
    setBulkActionMessage('');
    if (isBulk) {
      setBulkBusy(true);
    } else {
      setRowActionBusyId(String(targets[0]?.assetId ?? '').trim() || null);
    }

    let successCount = 0;
    const failures = [];
    const deletedKeys = [];

    for (const row of targets) {
      const id = String(row?.assetId ?? '').trim();
      if (!id) continue;
      try {
        await deleteInventoryAsset(id);
        successCount += 1;
        deletedKeys.push(assetRowKey(row));
        if (String(assetDetailTargetId || '') === id) {
          setAssetDetailOpen(false);
          setAssetDetailTargetId(null);
        }
      } catch {
        failures.push(row.hostname || row.ipAddress || id);
      }
    }

    if (deletedKeys.length > 0) {
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        deletedKeys.forEach((key) => next.delete(key));
        return next;
      });
    }

    if (failures.length === 0) {
      setBulkActionMessage(
        successCount === 1
          ? `Deleted ${targets[0]?.hostname || targets[0]?.ipAddress || 'asset'}.`
          : `Deleted ${successCount} assets.`
      );
      if (isBulk) setSelectedKeys(new Set());
    } else {
      setBulkActionMessage(
        `Deleted ${successCount}; failed for ${failures.length}: ${failures.slice(0, 3).join(', ')}${
          failures.length > 3 ? '…' : ''
        }`
      );
    }

    if (typeof onInventoryRefresh === 'function') {
      try {
        await onInventoryRefresh();
      } catch {
        /* parent may still hold stale rows until next visit */
      }
    }

    setBulkBusy(false);
    setRowActionBusyId(null);
    setDeleteConfirm(null);
  }, [deleteConfirm, rowActionBusyId, bulkBusy, assetDetailTargetId, onInventoryRefresh]);

  const confirmExclusion = useCallback(async () => {
    if (!exclusionConfirm || bulkBusy || rowActionBusyId) return;
    const { source, wantExcluded, rows } = exclusionConfirm;
    const verb = wantExcluded ? 'Exclude' : 'Include';
    const targets = Array.isArray(rows) ? rows : [];
    if (targets.length === 0) {
      setExclusionConfirm(null);
      return;
    }

    setBulkActionMessage('');
    if (source === 'row') {
      setRowActionBusyId(String(targets[0]?.assetId ?? '').trim() || null);
    } else if (source === 'detail') {
      setAssetDetailLoading(true);
      setAssetDetailError(null);
    } else {
      setBulkBusy(true);
    }

    const nextOverrides = { ...exclusionOverrides };
    let successCount = 0;
    const failures = [];

    for (const row of targets) {
      const id = String(row.assetId).trim();
      try {
        const result = await toggleInventoryExclusion(id);
        const excluded = Boolean(result?.is_excluded);
        nextOverrides[id] = excluded;
        successCount += 1;
      } catch (err) {
        failures.push(row.hostname || row.ipAddress || id);
      }
    }

    setExclusionOverrides(nextOverrides);

    if (source === 'detail') {
      if (failures.length > 0) {
        setAssetDetailError(
          `Failed to ${verb.toLowerCase()} asset${failures.length > 1 ? 's' : ''}.`
        );
      } else if (successCount > 0) {
        const id = String(targets[0]?.assetId ?? '').trim();
        const excluded = Boolean(nextOverrides[id]);
        setAssetDetail((prev) => {
          if (!prev?.inventory) return prev;
          return {
            ...prev,
            inventory: {
              ...prev.inventory,
              is_excluded: excluded,
            },
          };
        });
      }
      setAssetDetailLoading(false);
    } else if (failures.length === 0) {
      if (source === 'row') {
        const label = targets[0]?.hostname || targets[0]?.ipAddress || 'asset';
        setBulkActionMessage(`${verb}d ${label}.`);
      } else if (source === 'bulk') {
        setBulkActionMessage(
          `${verb}d ${successCount} asset${successCount === 1 ? '' : 's'}.`
        );
        setSelectedKeys(new Set());
      }
    } else {
      setBulkActionMessage(
        `${verb}d ${successCount}; failed for ${failures.length}: ${failures.slice(0, 3).join(', ')}${
          failures.length > 3 ? '…' : ''
        }`
      );
    }

    if (typeof onInventoryRefresh === 'function') {
      try {
        await onInventoryRefresh();
      } catch {
        /* keep optimistic overrides */
      }
    }

    setBulkBusy(false);
    setRowActionBusyId(null);
    setExclusionConfirm(null);
  }, [
    exclusionConfirm,
    bulkBusy,
    rowActionBusyId,
    exclusionOverrides,
    onInventoryRefresh,
  ]);

  const [certSortConfig, setCertSortConfig] = useState({ key: 'subject', direction: 'asc' });
  const [vulnSortConfig, setVulnSortConfig] = useState({ key: 'date_time', direction: 'desc' });
  const [assetDetailVulnSearch, setAssetDetailVulnSearch] = useState('');
  const {
    widths: assetDetailCertColumnWidths,
    onResizeStart: onAssetDetailCertColumnResizeStart,
    tableMinWidth: assetDetailCertTableMinWidth,
    resizingKey: assetDetailCertColumnResizingKey,
  } = useTableColumnResize(ASSET_DETAIL_CERT_TABLE_COLUMNS, ASSET_DETAIL_CERT_TABLE_COLUMN_WIDTHS_KEY);
  const {
    widths: assetDetailVulnColumnWidths,
    onResizeStart: onAssetDetailVulnColumnResizeStart,
    tableMinWidth: assetDetailVulnTableMinWidth,
    resizingKey: assetDetailVulnColumnResizingKey,
  } = useTableColumnResize(ASSET_DETAIL_VULN_TABLE_COLUMNS, ASSET_DETAIL_VULN_TABLE_COLUMN_WIDTHS_KEY);

  const openAssetDetail = async (row) => {
    setAssetDetailVulnSearch('');
    const assetId = row?.assetId;
    setAssetDetailTargetId(assetId ?? null);
    const isDemoAsset = assetId && String(assetId).startsWith('demo-');
    if (isDemoAsset) {
      setAssetDetailOpen(true);
      setAssetDetailLoading(false);
      setAssetDetailError('Asset detail is unavailable because this row is not linked to backend data.');
      setAssetDetail({
        inventory: {
          uu_id: assetId || null,
          ip: row?.ipAddress || '',
          hostname: row?.hostname || '',
          site_name: row?.site || '',
          host_os: row?.os || '',
          mac_address: row?.macAddress || '',
          mac_vendor: row?.manufacturer || '',
          host_software: row?.software || '',
          open_ports: row?.openPorts || '',
          last_scan_date: row?.lastScan || '',
          total_vul: Number(row?.vulnerabilities || 0) || 0,
        },
        certificate_info: [],
        vulnerabilities: [],
      });
      return;
    }
    if (!assetId) {
      const hasHostFallback = Boolean(String(row?.ipAddress || '').trim() || String(row?.hostname || '').trim());
      if (!hasHostFallback) {
        setAssetDetailOpen(true);
        setAssetDetailLoading(false);
        setAssetDetailError('Asset detail is unavailable because this row is not linked to backend data.');
        setAssetDetail(null);
        return;
      }
    }

    setAssetDetailOpen(true);
    setAssetDetailLoading(false);
    setAssetDetailError(null);
    // Paint from the inventory row immediately — no page loader inside the modal.
    setAssetDetail({
      inventory: {
        uu_id: assetId || null,
        ip: row?.ipAddress || '',
        hostname: row?.hostname || '',
        site_name: row?.site || '',
        host_os: row?.os || '',
        mac_address: row?.macAddress || '',
        mac_vendor: row?.manufacturer || '',
        host_software: row?.software || '',
        open_ports: row?.openPorts || '',
        last_scan_date: row?.lastScan || '',
        total_vul: Number(row?.vulnerabilities || 0) || 0,
        is_excluded: String(row?.ipExclusion || '').toLowerCase() === 'yes',
      },
      certificate_info: [],
      vulnerabilities: [],
    });

    try {
      const detailQuery = assetId
        ? { uu_id: assetId, inventory_uuid: assetId, id: assetId }
        : {
            site_uuid: String(row?.siteUuid || '').trim(),
            ip: String(row?.ipAddress || '').trim(),
            hostname: String(row?.hostname || '').trim(),
          };

      const d = await getInventoryDetail(null, detailQuery);
      if (d) setAssetDetail(d);
    } catch (e) {
      setAssetDetailError(e?.message || 'Failed to load asset detail');
    }
  };

  const closeAssetDetail = () => {
    setAssetDetailOpen(false);
    setAssetDetailTargetId(null);
    setAssetDetailVulnSearch('');
    if (typeof onDetailClose === 'function') onDetailClose();
  };

  const assetDetailOpenRef = useRef(assetDetailOpen);
  assetDetailOpenRef.current = assetDetailOpen;
  openAssetDetailRef.current = openAssetDetail;
  closeAssetDetailRef.current = closeAssetDetail;

  // Drill-in from dashboard Top Vulnerable Hosts / Assets ?q=: open detail (and optionally seed search).
  useEffect(() => {
    const nonce = focusSeed?.nonce;
    if (nonce == null || nonce === '' || nonce === lastFocusSeedNonceRef.current) return;

    const ip = String(focusSeed?.ip || '').trim();
    const hostname = String(focusSeed?.hostname || '').trim();
    const id = String(focusSeed?.id || '').trim();
    const searchQ = ip || hostname;
    if (!searchQ && !id) {
      lastFocusSeedNonceRef.current = nonce;
      return;
    }

    // Dashboard modal-only host: open detail immediately without navigating/filtering the table.
    if (modalOnly) {
      lastFocusSeedNonceRef.current = nonce;
      openAssetDetailRef.current?.({
        assetId: id || null,
        ipAddress: ip,
        hostname,
      });
      return;
    }

    if (searchQ) setTableSearch(searchQ);

    // Wait until inventory finished loading so we can prefer a real inventory row.
    if (isLoading) return;

    lastFocusSeedNonceRef.current = nonce;

    const needleIp = ip.toLowerCase();
    const needleHost = hostname.toLowerCase();
    const match =
      rows.find((r) => {
        const rowId = String(r?.assetId || '').trim();
        if (id && rowId && rowId === id) return true;
        const rowIp = String(r?.ipAddress || '').trim().toLowerCase();
        if (needleIp && rowIp && rowIp === needleIp) return true;
        const rowHost = String(r?.hostname || '').trim().toLowerCase();
        return Boolean(needleHost && rowHost && rowHost === needleHost);
      }) || null;

    if (match) {
      openAssetDetailRef.current?.(match);
      return;
    }

    // Inventory may not list this host yet; still open detail via IP/hostname fallback.
    openAssetDetailRef.current?.({
      assetId: id || null,
      ipAddress: ip,
      hostname,
    });
  }, [focusSeed, isLoading, rows, modalOnly]);

  useEffect(() => {
    const openFirst = () => {
      if (assetDetailOpenRef.current) return;
      const row = tourAssetRowsRef.current[0];
      if (!row) return;
      openAssetDetailRef.current?.(row);
    };
    const closeDetail = () => {
      if (!assetDetailOpenRef.current) return;
      closeAssetDetailRef.current?.();
    };
    const selectFirst = () => {
      const row = tourAssetRowsRef.current[0];
      if (!row) return;
      setSelectedKeys(new Set([assetRowKey(row)]));
    };
    const clearSelection = () => {
      setSelectedKeys(new Set());
    };
    window.addEventListener('xspectre:tour-open-first-asset', openFirst);
    window.addEventListener('xspectre:tour-close-asset-detail', closeDetail);
    window.addEventListener('xspectre:tour-select-first-asset', selectFirst);
    window.addEventListener('xspectre:tour-clear-asset-selection', clearSelection);
    return () => {
      window.removeEventListener('xspectre:tour-open-first-asset', openFirst);
      window.removeEventListener('xspectre:tour-close-asset-detail', closeDetail);
      window.removeEventListener('xspectre:tour-select-first-asset', selectFirst);
      window.removeEventListener('xspectre:tour-clear-asset-selection', clearSelection);
    };
  }, []);

  const handleExcludeAsset = () => {
    if (assetDetailLoading || bulkBusy || rowActionBusyId) return;
    const targetId = assetDetail?.inventory?.uu_id ?? assetDetailTargetId;
    const detailSource = assetDetail?.inventory?.detail_source || 'inventory';
    if (detailSource !== 'inventory') {
      setAssetDetailError('Exclusion is only available for inventory-backed assets.');
      return;
    }
    if (!targetId || String(targetId).startsWith('demo-')) {
      setAssetDetailError('Asset exclusion is unavailable because this row is not linked to an inventory record.');
      return;
    }
    const isExcluded = Boolean(assetDetail?.inventory?.is_excluded);
    setExclusionConfirm({
      source: 'detail',
      wantExcluded: !isExcluded,
      rows: [
        {
          assetId: targetId,
          hostname: assetDetail?.inventory?.hostname || '',
          ipAddress: assetDetail?.inventory?.ip || '',
        },
      ],
    });
  };

  useEffect(() => {
    if (!exclusionConfirm) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeExclusionConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [exclusionConfirm, closeExclusionConfirm]);

  useEffect(() => {
    if (!deleteConfirm) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeDeleteConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deleteConfirm, closeDeleteConfirm]);

  useEffect(() => {
    if (!assetDetailOpen || exclusionConfirm || deleteConfirm) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeAssetDetail();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [assetDetailOpen, exclusionConfirm, deleteConfirm]);

  const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toISOString().slice(0, 19).replace('T', ' ');
  };

  const toRowValues = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
    if (typeof value === 'object') return Object.values(value).map((v) => String(v).trim()).filter(Boolean);
    return String(value)
      .split(/[,\n]/)
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const assetDetailOpenPorts = toRowValues(assetDetail?.inventory?.open_ports);

  const openAssetVulnDetail = useCallback((vuln) => {
    const id = String(vuln?.vuln_id || '').trim();
    if (!id) return false;
    const assetIp = String(assetDetail?.inventory?.ip || '').trim();
    const assetHostname = String(assetDetail?.inventory?.hostname || '').trim();
    const assetOs = String(assetDetail?.inventory?.host_os || '').trim();
    const hinted = String(vuln?.scanner_type || '').trim().toLowerCase();
    // Asset inventory findings are network hosts unless explicitly marked web.
    const scannerType =
      hinted === 'web' || hinted.includes('zap')
        ? 'web'
        : 'network';
    const ip = String(vuln?.ip || assetIp || '').trim();
    const hostname = String(vuln?.hostname || assetHostname || '').trim();
    const hostOs = String(vuln?.host_os || assetOs || '').trim();
    navigate(riskDetailPath(id, scannerType), {
      state: {
        riskRow: {
          id,
          title: String(vuln?.title || '').trim() || 'Untitled vulnerability',
          source: scannerType === 'network' ? 'Network' : 'Web',
          scannerType,
          ip,
          hostname,
          target: ip || hostname || '—',
          port: String(vuln?.port || '').trim(),
          cvssScore: vuln?.cvss_score ?? vuln?.severity_float ?? null,
          raw: {
            ...vuln,
            scanner_type: scannerType,
            ip: ip || null,
            hostname: hostname || null,
            host_os: hostOs || null,
            mac_address: assetDetail?.inventory?.mac_address || vuln?.mac_address || null,
            mac_vendor: assetDetail?.inventory?.mac_vendor || vuln?.mac_vendor || null,
            scan_definition: vuln?.scan_definition || ip || null,
          },
        },
      },
    });
    return true;
  }, [assetDetail, navigate]);

  const openSoftwareItem = useCallback((name) => {
    const needle = String(name || '').trim();
    if (!needle) return;
    const vulns = Array.isArray(assetDetail?.vulnerabilities) ? assetDetail.vulnerabilities : [];
    const lower = needle.toLowerCase();
    const match =
      vulns.find((v) => String(v?.title || '').trim().toLowerCase() === lower) ||
      vulns.find((v) => String(v?.title || '').trim().toLowerCase().includes(lower));
    if (match && openAssetVulnDetail(match)) return;
    window.open(
      `https://nvd.nist.gov/vuln/search/results?form_type=Basic&results_type=overview&query=${encodeURIComponent(needle)}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, [assetDetail, openAssetVulnDetail]);

  const compareValues = (a, b, key) => {
    const aVal = a?.[key];
    const bVal = b?.[key];

    if (key === 'not_valid_before' || key === 'not_valid_after' || key === 'date_time') {
      const aTime = Date.parse(String(aVal ?? '')) || 0;
      const bTime = Date.parse(String(bVal ?? '')) || 0;
      return aTime - bTime;
    }

    if (key === 'cvss_score' || key === 'severity_float') {
      const aNum = Number(aVal) || 0;
      const bNum = Number(bVal) || 0;
      return aNum - bNum;
    }

    const aStr = String(aVal ?? '').toLowerCase();
    const bStr = String(bVal ?? '').toLowerCase();
    return aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
  };

  const sortedCertificates = useMemo(() => {
    const rows = Array.isArray(assetDetail?.certificate_info) ? [...assetDetail.certificate_info] : [];
    rows.sort((a, b) => {
      const result = compareValues(a, b, certSortConfig.key);
      return certSortConfig.direction === 'asc' ? result : -result;
    });
    return rows;
  }, [assetDetail, certSortConfig]);

  const assetDetailVulnerabilities = useMemo(() => {
    const rows = Array.isArray(assetDetail?.vulnerabilities) ? [...assetDetail.vulnerabilities] : [];
    rows.sort((a, b) => {
      const key = vulnSortConfig.key;
      const result = compareValues(a, b, key);
      return vulnSortConfig.direction === 'asc' ? result : -result;
    });
    return rows;
  }, [assetDetail, vulnSortConfig]);

  const filteredAssetDetailVulnerabilities = useMemo(() => {
    const query = String(assetDetailVulnSearch || '').trim().toLowerCase();
    if (!query) return assetDetailVulnerabilities;
    return assetDetailVulnerabilities.filter((row) => {
      const haystack = [
        row?.title,
        row?.severity,
        row?.port,
        row?.date_time,
        row?.cvss_score,
        row?.severity_float,
        row?.vuln_id,
      ]
        .map((value) => String(value ?? '').trim().toLowerCase())
        .filter(Boolean)
        .join(' ');
      return haystack.includes(query);
    });
  }, [assetDetailVulnerabilities, assetDetailVulnSearch]);

  const toggleCertSort = (key) => {
    setCertSortConfig((prev) => (prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }));
  };

  const toggleVulnSort = (key) => {
    setVulnSortConfig((prev) => (prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }));
  };

  const getDetailSortIcon = useCallback(
    (activeKey, sortState) => getTableSortIcon(sortState, activeKey),
    []
  );
  const getCertSortIcon = useCallback(
    (key) => getDetailSortIcon(key, certSortConfig),
    [certSortConfig, getDetailSortIcon]
  );
  const getVulnSortIcon = useCallback(
    (key) => getDetailSortIcon(key, vulnSortConfig),
    [vulnSortConfig, getDetailSortIcon]
  );

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [isLoading, sortedRows.length]);

  const assetsTableClassName =
    'assets-table assets-inventory-table assets-inventory-table--resizable risks-page-table';
  const assetsTableStyle = { width: '100%', minWidth: assetsTableMinWidth };
  const assetsColCount = assetColumns.length;
  const activeSearchQuery = String(tableSearch || universalSearchQuery || '').trim();
  const emptyMessage =
    activeSearchQuery && !isLoading && rows.length > 0 && sortedRows.length === 0
      ? 'No assets match your search'
      : 'No assets found';

  if (isLoading && !modalOnly) {
    return (
      <section className="assets-section" data-tour="assets-inventory">
        <PageDataLoader label="Loading assets" />
      </section>
    );
  }

  return (
    <section
      className={modalOnly ? 'assets-section assets-section--modal-only' : 'assets-section'}
      data-tour="assets-inventory"
    >
      {!modalOnly ? (
      <>
      <div className="assets-table-toolbar" data-tour="assets-toolbar">
        {selectedCount > 0 ? (
          <div className="assets-bulk-bar" role="status" aria-live="polite" data-tour="assets-bulk-actions">
            <button
              type="button"
              className="assets-bulk-exclude-btn"
              onClick={requestBulkExclusion}
              disabled={bulkBusy || bulkExcludableRows.length === 0 || Boolean(deleteConfirm)}
              title={
                bulkExcludableRows.length === 0
                  ? 'Exclusion requires inventory-backed assets'
                  : `${bulkExclusionLabel} ${selectedCount} selected asset${selectedCount === 1 ? '' : 's'}`
              }
            >
              {bulkBusy && exclusionConfirm
                ? `${bulkExclusionLabel}…`
                : `${bulkExclusionLabel} (${selectedCount})`}
            </button>
            <button
              type="button"
              className="assets-bulk-delete-btn"
              onClick={requestBulkDelete}
              disabled={bulkBusy || bulkExcludableRows.length === 0 || Boolean(exclusionConfirm)}
              title={
                bulkExcludableRows.length === 0
                  ? 'Delete requires inventory-backed assets'
                  : `Delete ${selectedCount} selected asset${selectedCount === 1 ? '' : 's'}`
              }
            >
              {`Delete (${selectedCount})`}
            </button>
          </div>
        ) : (
          <div className="assets-bulk-bar assets-bulk-bar--spacer" aria-hidden="true" />
        )}
        <div className="subscription-org-management-search" data-tour="assets-search">
          <label htmlFor={tableSearchId} className="edi-sr-only">
            Search assets
          </label>
          <div className="subscription-org-management-search-inner">
            <input
              id={tableSearchId}
              type="search"
              className="subscription-org-management-search-input"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search assets"
              autoComplete="off"
              spellCheck={false}
            />
            {tableSearch ? (
              <button
                type="button"
                className="ghost-exposure-sys-table-search-clear"
                onClick={() => setTableSearch('')}
                aria-label="Clear search"
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {bulkActionMessage ? (
        <div className="assets-bulk-message" role="status" aria-live="polite">
          {bulkActionMessage}
        </div>
      ) : null}
      <div className="assets-table-shell assets-table-shell--inventory-body" data-tour="assets-table">
        {isLoading ? (
          <div className="risks-page-table-loading">
            <BlueXLoader />
          </div>
        ) : (
          <>
            <div
              className="risks-page-table-header-frame"
              style={{ '--risks-header-gutter': `${headerGutterPx}px` }}
            >
              <div ref={tableHeaderRef} className="risks-page-table-header">
                <table className={`${assetsTableClassName} risks-page-table--header`} style={assetsTableStyle}>
                  <ResizableColgroup columns={assetColumns} widths={assetColumnWidths} />
                  <ResizableTableHead
                    columns={assetColumns}
                    widths={assetColumnWidths}
                    onResizeStart={onAssetColumnResizeStart}
                    resizingKey={assetColumnResizingKey}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    getSortIcon={getSortIcon}
                    resizeHandleClassName="risks-page-col-resize-handle"
                  />
                </table>
              </div>
              <div className="risks-page-table-header-gutter" aria-hidden="true" />
            </div>
            <div
              ref={tableScrollRef}
              className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
              onScroll={handleTableScroll}
            >
              <table className={`${assetsTableClassName} risks-page-table--body`} style={assetsTableStyle}>
                <ResizableColgroup columns={assetColumns} widths={assetColumnWidths} />
                <tbody>
                  {sortedRows.length > 0 ? (
                    visibleAssetRows.map((r, idx) => {
                      const key = assetRowKey(r);
                      const isSelected = selectedKeys.has(key);
                      const assetId = String(r?.assetId ?? '').trim();
                      const canExclude = Boolean(assetId) && !assetId.startsWith('demo-');
                      const isExcluded = String(r.ipExclusion || '').toLowerCase() === 'yes';
                      const exclusionVerb = isExcluded ? 'Include' : 'Exclude';
                      const exclusionBusy = rowActionBusyId === assetId;
                      const menuKey = `${key}:${idx}`;
                      const menuOpen = openRowMenuKey === menuKey;
                      const actionsDisabled = !canExclude || exclusionBusy || bulkBusy;
                      return (
                      <tr
                        key={`${key}-${idx}`}
                        className={[
                          'assets-row-clickable',
                          isSelected ? 'assets-row-selected' : null,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => openAssetDetail(r)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openAssetDetail(r);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`View asset details for ${r.hostname || r.ipAddress || key}`}
                        data-tour={idx === 0 ? 'assets-row' : undefined}
                      >
                        <td
                          className="risks-page-td-checkbox assets-cell-center"
                          onClick={(e) => e.stopPropagation()}
                          data-tour={idx === 0 ? 'assets-select' : undefined}
                        >
                          <input
                            type="checkbox"
                            className="risks-page-checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelected(key)}
                            aria-label={`Select asset ${r.hostname || r.ipAddress || key}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td data-tour={idx === 0 ? 'assets-row-open' : undefined}>
                          {(() => {
                            const org = String(r.org || '').trim();
                            const site = String(r.site || '').trim();
                            if (!org && !site) return displayOrNA('');
                            const title = [org, site].filter(Boolean).join(' / ');
                            return (
                              <span className="assets-org-site-stack" title={title}>
                                <span className="assets-org-site-org">{org || '—'}</span>
                                {site ? <span className="assets-org-site-site">{site}</span> : null}
                              </span>
                            );
                          })()}
                        </td>
                        <td>{displayOrNA(r.ipAddress)}</td>
                        <td className="assets-cell-ellipsis">{displayOrNA(r.hostname)}</td>
                        <td>{displayOrNA(r.type)}</td>
                        <td>{displayOrNA(r.macAddress)}</td>
                        <td className="assets-certificates-cell">{r.certificates ?? 0}</td>
                        <td
                          className="assets-action-cell"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="assets-row-actions"
                            data-tour={idx === 0 ? 'assets-row-actions' : undefined}
                          >
                            <button
                              type="button"
                              className="assets-delete-btn"
                              onClick={() => {
                                closeRowActionsMenu();
                                requestRowDelete(r);
                              }}
                              disabled={actionsDisabled || Boolean(exclusionConfirm)}
                              aria-label={`Delete ${r.hostname || r.ipAddress || key}`}
                              title="Delete asset"
                            >
                              <TrashIcon className="assets-delete-btn-icon" size={14} strokeWidth={2.35} />
                            </button>
                            <div
                              className="assets-row-actions-menu-wrap"
                              ref={menuOpen ? rowActionsTriggerRef : null}
                            >
                              <button
                                type="button"
                                className={[
                                  'assets-more-btn',
                                  menuOpen ? 'assets-more-btn--open' : null,
                                ].filter(Boolean).join(' ')}
                                title="More actions"
                                aria-label={`More actions for ${r.hostname || r.ipAddress || key}`}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                disabled={actionsDisabled || Boolean(deleteConfirm)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openRowMenuKey === menuKey) {
                                    setOpenRowMenuKey(null);
                                    setRowMenuPlacement(null);
                                    return;
                                  }
                                  const trigger = e.currentTarget;
                                  if (!trigger) return;
                                  const rect = trigger.getBoundingClientRect();
                                  const estimatedMenuHeight = 52;
                                  const openUp = window.innerHeight - rect.bottom < estimatedMenuHeight + 16;
                                  const isLightTheme = Boolean(
                                    document.querySelector('.dashboard-container.theme-light')
                                  );
                                  setRowMenuPlacement({
                                    top: openUp ? rect.top - 4 : rect.bottom + 4,
                                    right: Math.max(8, window.innerWidth - rect.right),
                                    openUp,
                                    isLightTheme,
                                  });
                                  setOpenRowMenuKey(menuKey);
                                }}
                              >
                                <svg
                                  className="assets-more-btn-icon"
                                  viewBox="0 0 20 20"
                                  width="14"
                                  height="14"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <circle cx="4" cy="10" r="1.6" />
                                  <circle cx="10" cy="10" r="1.6" />
                                  <circle cx="16" cy="10" r="1.6" />
                                </svg>
                              </button>
                              {menuOpen && rowMenuPlacement
                                ? createPortal(
                                    <div
                                      ref={rowActionsMenuRef}
                                      className={[
                                        'assets-row-actions-menu',
                                        'assets-row-actions-menu--portal',
                                        rowMenuPlacement.openUp ? 'assets-row-actions-menu--up' : null,
                                        rowMenuPlacement.isLightTheme
                                          ? 'assets-row-actions-menu--light'
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      role="menu"
                                      style={{
                                        top: rowMenuPlacement.top,
                                        right: rowMenuPlacement.right,
                                      }}
                                    >
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className="assets-row-actions-menu-item"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          closeRowActionsMenu();
                                          requestRowExclusion(r);
                                        }}
                                      >
                                        {exclusionVerb}
                                      </button>
                                    </div>,
                                    document.body
                                  )
                                : null}
                            </div>
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={assetsColCount} className="assets-empty-cell">{emptyMessage}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      {assetPagination.total > assetPagination.pageSize ? (
        <div data-tour="assets-pagination">
          <TablePagination {...assetPagination} />
        </div>
      ) : (
        <TablePagination {...assetPagination} />
      )}
      </>
      ) : null}

      {assetDetailOpen && (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAssetDetail();
          }}
        >
          <div
            className="assets-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Asset detail"
            data-tour="assets-detail-modal"
          >
            <div className="assets-detail-modal-inner">
            <div className="assets-detail-modal-header" data-tour="assets-detail-header">
              <div className="assets-detail-modal-title">Asset Detail</div>
              <div className="assets-detail-modal-actions" data-tour="assets-detail-actions">
                <button
                  type="button"
                  className="assets-detail-modal-exclude"
                  onClick={handleExcludeAsset}
                  disabled={assetDetailLoading}
                  data-tour="assets-detail-exclude"
                >
                  {assetDetail?.inventory?.is_excluded ? 'Include' : 'Exclude'}
                </button>
                <button type="button" className="assets-detail-modal-close" onClick={closeAssetDetail} aria-label="Close">
                  ✕
                </button>
              </div>
            </div>

            {assetDetailError ? (
              <div className="assets-detail-error" role="alert">
                {assetDetailError}
              </div>
            ) : null}

            {assetDetail ? (
              <>
                <div className="assets-detail-basic-grid" data-tour="assets-detail-summary">
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'IP Address'}</div>
                    <div className="assets-detail-v">{displayOrNA(assetDetail?.inventory?.ip)}</div>
                  </div>
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'Hostname'}</div>
                    <div className="assets-detail-v">{displayOrNA(assetDetail?.inventory?.hostname)}</div>
                  </div>
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'Site'}</div>
                    <div className="assets-detail-v">{displayOrNA(assetDetail?.inventory?.site_name)}</div>
                  </div>
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'Operating System'}</div>
                    <div className="assets-detail-v">{displayOrNA(assetDetail?.inventory?.host_os)}</div>
                  </div>
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'MAC Address'}</div>
                    <div className="assets-detail-v">{displayOrNA(assetDetail?.inventory?.mac_address)}</div>
                  </div>
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'Manufacturer'}</div>
                    <div className="assets-detail-v">{displayOrNA(assetDetail?.inventory?.mac_vendor)}</div>
                  </div>
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'Vulnerabilities'}</div>
                    <div className="assets-detail-v">{assetDetail?.inventory?.total_vul ?? 0}</div>
                  </div>
                  <div className="assets-detail-kv">
                    <div className="assets-detail-k">{'Last Scan'}</div>
                    <div className="assets-detail-v">{formatDateTime(assetDetail?.inventory?.last_scan_date)}</div>
                  </div>
                </div>

                <div className="assets-detail-dual-tables" data-tour="assets-detail-software-ports">
                  <div className="assets-detail-dual-item">
                    <div className="assets-detail-subtitle">Software</div>
                    <AssetDetailEmbeddedTable
                      hideHeader
                      emptyMessage="No software found"
                      className="assets-detail-embedded-table-shell--compact"
                    >
                      {toRowValues(assetDetail?.inventory?.host_software).length
                        ? toRowValues(assetDetail?.inventory?.host_software).map((item, idx) => (
                            <tr
                              key={`software-${idx}`}
                              className="assets-row-clickable"
                              onClick={() => openSoftwareItem(item)}
                            >
                              <td>
                                <button
                                  type="button"
                                  className="vuln-title-link assets-detail-software-link"
                                  title={`View details for ${item}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openSoftwareItem(item);
                                  }}
                                >
                                  {item}
                                </button>
                              </td>
                            </tr>
                          ))
                        : null}
                    </AssetDetailEmbeddedTable>
                  </div>

                  <div className="assets-detail-dual-item">
                    <div className="assets-detail-subtitle">Open Ports</div>
                    <div
                      className="assets-table-shell assets-table-shell--inventory-body assets-detail-embedded-table-shell assets-detail-embedded-table-shell--no-header assets-detail-embedded-table-shell--compact assets-detail-port-panel"
                    >
                      {assetDetailOpenPorts.length ? (
                        <div className="assets-detail-port-tags" role="list" aria-label="Open ports">
                          {assetDetailOpenPorts.map((item, idx) => (
                            <span
                              key={`open-port-${idx}`}
                              className="assets-detail-port-tag"
                              role="listitem"
                              title={item}
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="assets-detail-empty-inline">No open ports found</div>
                      )}
                    </div>
                  </div>
                </div>

                <div data-tour="assets-detail-certificates">
                <div className="assets-detail-subtitle">Certificates</div>
                <AssetDetailEmbeddedTable
                  columns={ASSET_DETAIL_CERT_TABLE_COLUMNS}
                  widths={assetDetailCertColumnWidths}
                  onResizeStart={onAssetDetailCertColumnResizeStart}
                  resizingKey={assetDetailCertColumnResizingKey}
                  sortConfig={certSortConfig}
                  onSort={toggleCertSort}
                  getSortIcon={getCertSortIcon}
                  tableMinWidth={assetDetailCertTableMinWidth}
                  emptyMessage="No certificates found"
                >
                  {sortedCertificates.length
                    ? sortedCertificates.map((c, idx) => (
                        <tr key={`${c.subject || 'cert'}-${idx}`}>
                          <td>{c.subject || ''}</td>
                          <td>{Array.isArray(c.sans) ? c.sans.join(', ') : ''}</td>
                          <td>{c.issuer || ''}</td>
                          <td>{formatDateTime(c.not_valid_before)}</td>
                          <td>{formatDateTime(c.not_valid_after)}</td>
                          <td>{Array.isArray(c.ports) ? c.ports.join(', ') : ''}</td>
                        </tr>
                      ))
                    : null}
                </AssetDetailEmbeddedTable>
                </div>

                <div data-tour="assets-detail-vulnerabilities">
                <div className="assets-detail-subtitle-row">
                  <div className="assets-detail-subtitle">Vulnerabilities</div>
                  <div className="assets-detail-subtitle-actions">
                    <div className="subscription-org-management-search assets-detail-vuln-search">
                      <label htmlFor={assetDetailVulnSearchId} className="edi-sr-only">
                        Search vulnerabilities in this asset
                      </label>
                      <div className="subscription-org-management-search-inner">
                        <input
                          id={assetDetailVulnSearchId}
                          type="search"
                          className="subscription-org-management-search-input"
                          value={assetDetailVulnSearch}
                          onChange={(e) => setAssetDetailVulnSearch(e.target.value)}
                          placeholder="Search vulnerabilities"
                          autoComplete="off"
                          spellCheck={false}
                        />
                        {assetDetailVulnSearch ? (
                          <button
                            type="button"
                            className="ghost-exposure-sys-table-search-clear"
                            onClick={() => setAssetDetailVulnSearch('')}
                            aria-label="Clear vulnerability search"
                          >
                            ✕
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
                <AssetDetailEmbeddedTable
                  className="assets-detail-embedded-table-shell--vuln-scroll"
                  columns={ASSET_DETAIL_VULN_TABLE_COLUMNS}
                  widths={assetDetailVulnColumnWidths}
                  onResizeStart={onAssetDetailVulnColumnResizeStart}
                  resizingKey={assetDetailVulnColumnResizingKey}
                  sortConfig={vulnSortConfig}
                  onSort={toggleVulnSort}
                  getSortIcon={getVulnSortIcon}
                  tableMinWidth={assetDetailVulnTableMinWidth}
                  emptyMessage={
                    assetDetailVulnerabilities.length && assetDetailVulnSearch.trim()
                      ? 'No vulnerabilities match your search'
                      : 'No vulnerabilities found'
                  }
                >
                  {filteredAssetDetailVulnerabilities.length
                    ? filteredAssetDetailVulnerabilities.map((v, idx) => (
                        <tr
                          key={`${v.vuln_id || 'vuln'}-${idx}`}
                          className={v.vuln_id ? 'assets-row-clickable' : undefined}
                          onClick={v.vuln_id ? () => openAssetVulnDetail(v) : undefined}
                        >
                          <td>
                            {v.vuln_id ? (
                              <button
                                type="button"
                                className="vuln-title-link assets-detail-software-link"
                                title={`View vulnerability details for ${v.title || 'this finding'}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openAssetVulnDetail(v);
                                }}
                              >
                                {v.title || ''}
                              </button>
                            ) : (
                              v.title || ''
                            )}
                          </td>
                          <td>{formatDateTime(v.date_time)}</td>
                          <td>{v.severity || ''}</td>
                          <td>
                            {(() => {
                              const score = v.cvss_score ?? v.severity_float;
                              if (score == null || score === '') return '';
                              return (
                                <span className={`vuln-severity-badge ${severityBadgeClass(score)}`}>
                                  {score}
                                </span>
                              );
                            })()}
                          </td>
                          <td>{v.port || ''}</td>
                        </tr>
                      ))
                    : null}
                </AssetDetailEmbeddedTable>
                </div>
              </>
            ) : null}
            </div>
          </div>
        </div>
      )}

      {exclusionConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeExclusionConfirm();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label={
              exclusionConfirm.wantExcluded ? 'Confirm exclude asset' : 'Confirm include asset'
            }
            aria-describedby={exclusionConfirmDescId}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeExclusionConfirm}
              aria-label="Close"
              disabled={exclusionConfirmBusy}
            >
              ✕
            </button>
            <p id={exclusionConfirmDescId} className="assets-exclude-confirm-desc">
              {(() => {
                const count = exclusionConfirm.rows?.length || 0;
                const first = exclusionConfirm.rows?.[0];
                const assetLabel =
                  first?.hostname || first?.ipAddress || first?.assetId || 'this asset';
                if (exclusionConfirm.wantExcluded) {
                  if (count === 1) {
                    return (
                      <>
                        Are you sure you want to exclude this asset?
                        <span className="assets-exclude-confirm-asset">{assetLabel}</span>
                        <span className="assets-exclude-confirm-warning">
                          This asset will be excluded from scanning.
                        </span>
                      </>
                    );
                  }
                  return (
                    <>
                      Are you sure you want to exclude {count} selected assets?
                      <span className="assets-exclude-confirm-warning">
                        These assets will be excluded from scanning.
                      </span>
                    </>
                  );
                }
                if (count === 1) {
                  return (
                    <>
                      Are you sure you want to include this asset?
                      <span className="assets-exclude-confirm-asset">{assetLabel}</span>
                      <span className="assets-exclude-confirm-warning">
                        This asset will be included back into scanning.
                      </span>
                    </>
                  );
                }
                return (
                  <>
                    Are you sure you want to include {count} selected assets?
                    <span className="assets-exclude-confirm-warning">
                      These assets will be included back into scanning.
                    </span>
                  </>
                );
              })()}
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn"
                onClick={confirmExclusion}
                disabled={exclusionConfirmBusy}
              >
                {exclusionConfirmBusy
                  ? exclusionConfirm.wantExcluded
                    ? 'Excluding…'
                    : 'Including…'
                  : exclusionConfirm.wantExcluded
                    ? 'Exclude'
                    : 'Include'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeDeleteConfirm();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Confirm delete asset"
            aria-describedby={deleteConfirmDescId}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeDeleteConfirm}
              aria-label="Close"
              disabled={deleteConfirmBusy}
            >
              ✕
            </button>
            <p id={deleteConfirmDescId} className="assets-exclude-confirm-desc">
              {(() => {
                const rows = Array.isArray(deleteConfirm.rows)
                  ? deleteConfirm.rows
                  : deleteConfirm.row
                    ? [deleteConfirm.row]
                    : [];
                const count = rows.length;
                const first = rows[0];
                const assetLabel =
                  first?.hostname || first?.ipAddress || first?.assetId || 'this asset';
                if (count <= 1) {
                  return (
                    <>
                      You are about to permanently delete this asset.
                      <span className="assets-exclude-confirm-asset">{assetLabel}</span>
                      <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
                    </>
                  );
                }
                return (
                  <>
                    You are about to permanently delete {count} assets.
                    <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
                  </>
                );
              })()}
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmDeleteAsset}
                disabled={deleteConfirmBusy}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AssetsTable;
export { VulnerabilitiesTable, buildDashboardVulnRows, buildAssetTableRows, universalSearchTextMatches, UNIVERSAL_SEARCH_RESULTS_CAP };
